import { Form, Formik, FormikProps } from 'formik';
import { useRef, useState } from 'react';
import { IoEyeOffOutline, IoEyeOutline } from 'react-icons/io5';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTheme } from 'styled-components';

import authApis from '../../../../APIs/auth';
import logo from '../../../../assets/icons/logo.svg';
import {
  Button,
  CodeInput,
  FormContainer,
  Input,
  Typography,
} from '../../../../designComponents';
import { LOGIN_ROUTE } from '../../../../routes/routePaths';
import { requestAPI } from '../../../../utils';
import { generatePassword } from '../../../../utils/generateRandomPassword';
import {
  ButtonContainer,
  ContentContainer,
  Heading,
  Label,
  LoginContainer,
  Logo,
} from './style';
import userDataValidation from './validation';
interface initialValuesTypes {
  userEmail: string;
  codeInput: string;
  newPassword: string;
  confirmNewPassword: string;
}
const ChangePassword = () => {
  const [loading, setLoading] = useState(false);
  const [isResendDisabled, setIsResendDisabled] = useState(false);
  const [isClickedEyeButton, setIsClickedEyeButton] = useState(false);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const email = queryParams.get('email');
  const [initialValues, _setIntialValues] = useState<initialValuesTypes>({
    userEmail: email || '',
    codeInput: '',
    newPassword: '',
    confirmNewPassword: '',
  });

  const navigate = useNavigate();
  const formikRef = useRef<FormikProps<initialValuesTypes>>(null);

  const theme = useTheme();

  const handleChangePassword = async (values: {
    codeInput: string;
    newPassword: string;
  }) => {
    try {
      setLoading(true);
      await requestAPI(
        authApis.resetPassword({
          otp: parseInt(values.codeInput, 10),
          emailAddress: email || '',
          newPassword: values.newPassword,
          otpType: 'RESET_PASSWORD',
        }),
      );
      setLoading(false);
      navigate(LOGIN_ROUTE);
      toast.success('Password reset successfully.');
    } catch (error: any) {
      setLoading(false);
      toast.error(error);
    }
  };

  const handleResendCode = async () => {
    try {
      setIsResendDisabled(true);
      await requestAPI(authApis.resendOtpWithEmail(email || ''));
      toast('Code sent to your email.');
      formikRef.current?.setFieldValue('codeInput', '');
      setTimeout(() => {
        setIsResendDisabled(false);
      }, 30000);
    } catch (error) {
      console.error('Failed to resend code:', error);
    }
  };

  const handleGenerateRandomPassword = () => {
    const password = generatePassword();
    if (formikRef.current) {
      formikRef.current.setFieldValue('newPassword', password);
      formikRef.current.setFieldValue('confirmNewPassword', password);
    }
  };
  return (
    <LoginContainer>
      <ContentContainer>
        <Heading>
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
          >
            <Typography variant="heading" strong>
              Evacuation Plans
            </Typography>
            <Typography style={{ marginLeft: '45px', color: '#3D3D3D' }}>
              SaaS Application
            </Typography>
          </div>
          <div>
            <Logo src={logo} alt="logo" />
          </div>
        </Heading>

        <Label>
          <Typography size={16} color={theme.colors.normalText} strong>
            Reset Password
          </Typography>
        </Label>

        <Typography style={{ marginBottom: '15px', textAlign: 'center' }}>
          We have sent you a verification code to your email address to reset
          your password.
        </Typography>
        <Formik
          initialValues={initialValues}
          onSubmit={handleChangePassword}
          validationSchema={userDataValidation.ChangePasswordSchema}
          enableReinitialize
          innerRef={formikRef}
        >
          {({ values, setFieldValue, errors, handleSubmit, touched }) => {
            return (
              <Form
                onSubmit={handleSubmit}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                }}
              >
                <div>
                  <FormContainer
                    label="Email"
                    error={touched?.userEmail ? errors.userEmail : ''}
                  >
                    <Input
                      name="userEmail"
                      value={values?.userEmail || ''}
                      disabled
                    />
                  </FormContainer>
                  <FormContainer
                    label="Enter OTP"
                    error={touched?.codeInput ? errors.codeInput : ''}
                  >
                    <CodeInput
                      name="codeInput"
                      value={values.codeInput}
                      onChange={(newValue) =>
                        setFieldValue('codeInput', newValue)
                      }
                      numberOfField={6}
                    />
                  </FormContainer>
                  <FormContainer
                    label="New password"
                    error={touched?.newPassword ? errors.newPassword : ''}
                  >
                    <Input
                      name="newPassword"
                      value={values.newPassword}
                      type={isClickedEyeButton ? 'text' : 'password'}
                      onChange={(e) =>
                        setFieldValue('newPassword', e.target.value)
                      }
                      placeholder="Enter new password"
                      iconRight={
                        <div
                          role="button"
                          onClick={() => setIsClickedEyeButton((prev) => !prev)}
                        >
                          {isClickedEyeButton ? (
                            <IoEyeOutline size={18} />
                          ) : (
                            <IoEyeOffOutline size={18} />
                          )}
                        </div>
                      }
                    />
                  </FormContainer>
                  <Button
                    onClick={handleGenerateRandomPassword}
                    variant="secondary"
                    bgColor={theme.colors.darkSlateGray.faded}
                    style={{ marginBottom: '20px' }}
                  >
                    Generate Password
                  </Button>
                  <FormContainer
                    label="Confirm New password"
                    error={
                      touched?.confirmNewPassword
                        ? errors.confirmNewPassword
                        : ''
                    }
                  >
                    <Input
                      name="confirmNewPassword"
                      type={isClickedEyeButton ? 'text' : 'password'}
                      value={values.confirmNewPassword}
                      onChange={(e) =>
                        setFieldValue('confirmNewPassword', e.target.value)
                      }
                      placeholder="Enter confirm password"
                      iconRight={
                        <div
                          role="button"
                          onClick={() => setIsClickedEyeButton((prev) => !prev)}
                        >
                          {isClickedEyeButton ? (
                            <IoEyeOutline size={18} />
                          ) : (
                            <IoEyeOffOutline size={18} />
                          )}
                        </div>
                      }
                    />
                  </FormContainer>
                </div>

                <ButtonContainer>
                  <Button
                    type="submit"
                    variant="secondary"
                    size="md"
                    isLoading={loading}
                  >
                    Reset Password
                  </Button>
                  <Button
                    onClick={handleResendCode}
                    variant="gray"
                    size="md"
                    isLoading={loading}
                    disabled={isResendDisabled}
                  >
                    Resend OTP
                  </Button>
                </ButtonContainer>
              </Form>
            );
          }}
        </Formik>
      </ContentContainer>
    </LoginContainer>
  );
};

export default ChangePassword;
